import { Client, DemSearchResponse, DemDownloadResponse } from "./Client";

export class FacilitiesService {
  constructor(private client: Client) {}

  async fetchFacilities() {
    return await this.client.fetchFacilities();
  }

  async fetchEmissionSource(facilityID: string) {
    return await this.client.fetchEmissionSource(facilityID);
  }

  async fetchStations(facilityID: string) {
    return await this.client.fetchStations(facilityID);
  }

  async postFacility(facility: any) {
    return await this.client.postFacility(facility);
  }

  async fetchSurfaceChars(facilityID: string) {
    return await this.client.fetchSurfaceChars(facilityID);
  }

  async patchFacility(facilityID: string, facility: any) {
    return await this.client.patchFacility(facilityID, facility);
  }

  async deleteFacility(facilityID: string) {
    return await this.client.deleteFacility(facilityID);
  }

  async deleteFacilityPreview(facilityID: string) {
    return await this.client.deleteFacilityPreview(facilityID);
  }

  async deleteFacilityCascade(facilityID: string, confirmationName: string) {
    return await this.client.deleteFacilityCascade(facilityID, confirmationName);
  }

  async createProject(project: any) {
    return await this.client.createProject(project);
  }

  async processAermet(facilityID: string, dateStart: string, dateEnd: string, avertime?: string, upperairSource?: string) {
    return await this.client.processAermet(facilityID, dateStart, dateEnd, avertime, upperairSource);
  }

  async fetchReceptors(facilityID: string) {
    return await this.client.fetchReceptors(facilityID);
  }

  async updateReceptors(facilityID: string, receptors: string, useManualReceptors: boolean) {
    return await this.client.updateReceptors(facilityID, receptors, useManualReceptors);
  }

  async fetchAutoReceptorPreview(facilityID: string) {
    return await this.client.fetchAutoReceptorPreview(facilityID);
  }

  async parseDiscreteReceptorFile(facilityID: string, file: File) {
    return await this.client.parseDiscreteReceptorFile(facilityID, file);
  }

  // ═══ Terrain Config (AERMAP) ═══

  async fetchTerrainConfig(facilityID: string) {
    return await this.client.fetchTerrainConfig(facilityID);
  }

  async fetchTerrainElevationGrid(facilityID: string) {
    return await this.client.fetchTerrainElevationGrid(facilityID);
  }

  async updateTerrainConfig(facilityID: string, config: any) {
    return await this.client.updateTerrainConfig(facilityID, config);
  }

  async fetchTerrainOutput(facilityID: string) {
    return await this.client.fetchTerrainOutput(facilityID);
  }

  async uploadTerrainDEM(facilityID: string, file: File, horizDatum?: string, vertDatum?: string) {
    return await this.client.uploadTerrainDEM(facilityID, file, horizDatum, vertDatum);
  }

  async deleteTerrainDEM(facilityID: string, fileId: string) {
    return await this.client.deleteTerrainDEM(facilityID, fileId);
  }

  async redetectTerrainDEM(facilityID: string, fileId: string) {
    return await this.client.redetectTerrainDEM(facilityID, fileId);
  }

  // ═══ DEM Search (fluxo 2 — busca/download automático de tiles SRTM) ═══

  async searchDems(facilityID: string): Promise<DemSearchResponse> {
    return await this.client.searchDems(facilityID);
  }

  async downloadDems(facilityID: string, tileIds: string[]): Promise<DemDownloadResponse> {
    return await this.client.downloadDems(facilityID, tileIds);
  }

  async runAermap(facilityID: string, config?: any) {
    return await this.client.runAermap(facilityID, config);
  }

  async preflightAermap(facilityID: string, config?: any) {
    return await this.client.preflightAermap(facilityID, config);
  }

  async previewAermapInp(facilityID: string, overrides?: any) {
    return await this.client.previewAermapInp(facilityID, overrides);
  }

  async fetchLastAermapJob(facilityID: string) {
    return await this.client.fetchLastAermapJob(facilityID);
  }

  // ═══ Background Jobs ═══

  async fetchJobs(facilityId?: string) {
    return await this.client.fetchJobs(facilityId);
  }

  async fetchJobStatus(jobId: string) {
    return await this.client.fetchJobStatus(jobId);
  }

  async cancelJob(jobId: string) {
    return await this.client.cancelJob(jobId);
  }

  // ═══ Map Config ═══

  async getMapConfig(facilityID: string) {
    return await this.client.getMapConfig(facilityID);
  }

  async saveMapConfig(facilityID: string, center_lat: number, center_lon: number, zoom: number) {
    return await this.client.saveMapConfig(facilityID, center_lat, center_lon, zoom);
  }

  async resetMapConfig(facilityID: string) {
    return await this.client.resetMapConfig(facilityID);
  }

  // ═══ Operators ═══

  async getOperators(facilityID: string) {
    const { data } = await this.client.api.get(`/facilities/${facilityID}/operators/`);
    return data;
  }

  async addOperator(facilityID: string, userId: string) {
    const { data } = await this.client.api.post(`/facilities/${facilityID}/operators/add/`, { user_id: userId });
    return data;
  }

  async removeOperator(facilityID: string, userId: string) {
    const { data } = await this.client.api.post(`/facilities/${facilityID}/operators/remove/`, { user_id: userId });
    return data;
  }

  async getAvailableUsers() {
    const { data } = await this.client.api.get(`/facilities/available-users/`);
    return data;
  }

  /** Importa um PLOTFILE (.PLT) externo como pluma offline. Um arquivo por chamada. */
  async importResultFile(
    facilityID: string,
    file: File,
    params: { zone: number; pollutant: string; scenario?: string; software?: string; ignoreDistance?: boolean },
  ) {
    const form = new FormData();
    form.append("file", file);
    form.append("zone", String(params.zone));
    form.append("pollutant", params.pollutant);
    if (params.scenario) form.append("scenario", params.scenario);
    if (params.software) form.append("software", params.software);
    if (params.ignoreDistance) form.append("ignore_distance", "true");
    const { data } = await this.client.api.post(`/facilities/${facilityID}/import-results/`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  }
}
